import {useQuery} from 'react-query';
import {AutomationExecution, ExecutionStatus} from 'crqlar-react-common/dist/models/automation/automation';
import {AutomationService} from 'crqlar-react-common/dist/services/automation/AutomationService';

// Counts come from four size-1 requests (`totalElements`); each returned row is the run the card names.
export interface FlowActivity {
    /** Claimed by no worker yet; a worker picks one up within about five seconds. */
    queued: number;
    running: number;
    /** The newest running run. */
    runningExecution?: AutomationExecution;
    waiting: number;
    /** The waiting run that resumes first. */
    waitingExecution?: AutomationExecution;
    /** The newest run in any state, possibly also the running or waiting one. */
    lastRun?: AutomationExecution;
}

export const FLOW_ACTIVITY_QUERY_KEY = 'automation-activity';

const BUSY_INTERVAL_MS = 5000;
const IDLE_INTERVAL_MS = 15000;

/** `enabled` false stops the polling, e.g. while the runs overlay covers the card. */
export function useFlowActivity(flowId?: string, enabled = true) {
    return useQuery<FlowActivity>(
        [FLOW_ACTIVITY_QUERY_KEY, flowId],
        async () => {
            const page = {flowId, page: 0, pageSize: 1};
            const [pending, running, waiting, latest] = await Promise.all([
                AutomationService.getExecutions({...page, status: ExecutionStatus.PENDING}),
                AutomationService.getExecutions({...page, status: ExecutionStatus.RUNNING}),
                // By resume time, so the row is the next one back.
                AutomationService.getExecutions({
                    ...page,
                    status: ExecutionStatus.WAITING,
                    sortBy: 'waitUntil',
                    sortDir: 'ASC'
                }),
                AutomationService.getExecutions(page)
            ]);

            return {
                queued: pending.totalElements ?? 0,
                running: running.totalElements ?? 0,
                runningExecution: running.content?.[0],
                waiting: waiting.totalElements ?? 0,
                waitingExecution: waiting.content?.[0],
                lastRun: latest.content?.[0]
            };
        },
        {
            enabled: !!flowId && enabled,
            refetchOnWindowFocus: true,
            // A hidden tab is not polled: react-query skips interval refetches without `refetchIntervalInBackground`.
            refetchInterval: data => data && data.queued + data.running > 0 ? BUSY_INTERVAL_MS : IDLE_INTERVAL_MS
        }
    );
}
